/*
  Rockets (Nostrocket kind 31108) and the problems they say they are solving.

  A rocket ignition or state event may carry one `["problem", <31971
  coordinate>, <relay?>]` tag: the problem that rocket exists to solve. That
  single link is enough to answer "which rockets are working on this problem?"
  for any card in a problem tree, without a schema of its own.

  Target-agnostic: structural NIP-01 shapes only, no shell imports.
*/
import { COORDINATE } from "./problem-dag";
import type { NostrEvent, NostrFilter, RelayEventResult } from "./nostr";

export const ROCKET_KIND = 31108;
/** How many kind 31108 events one rocket sweep asks for per relay. */
export const ROCKET_DISCOVERY_LIMIT = 500;

export interface RocketProblemReference {
  /** The `31971:<owner>:<problem-id>` coordinate the rocket links itself to. */
  coordinate: string;
  /** Optional relay hint carried in the third tag position. */
  relay?: string;
}

export interface Rocket {
  /** Addressable coordinate `31108:<author pubkey>:<identifier>`. */
  coordinate: string;
  /** The `d` tag value identifying this rocket. */
  identifier: string;
  author: string;
  mission?: string;
  problem?: RocketProblemReference;
  createdAt: number;
  eventId: string;
}

/** Filter for sweeping every discoverable rocket ignition. */
export function rocketFilters(): NostrFilter[] {
  return [{ kinds: [ROCKET_KIND], limit: ROCKET_DISCOVERY_LIMIT }];
}

const HEX_64 = /^[0-9a-f]{64}$/;

const tagValue = (event: NostrEvent, name: string): string | undefined => {
  const value = event.tags.find(([tag]) => tag === name)?.[1]?.trim();
  return value || undefined;
};

const problemReference = (event: NostrEvent): RocketProblemReference | undefined => {
  const tag = event.tags.find(([name]) => name === "problem");
  const coordinate = tag?.[1]?.trim().toLowerCase();
  if (!coordinate || !COORDINATE.test(coordinate)) return undefined;
  const relay = tag?.[2]?.trim();
  return relay ? { coordinate, relay } : { coordinate };
};

/** Parse one kind 31108 event into a rocket; undefined when it cannot name one. */
export function parseRocket(event: NostrEvent): Rocket | undefined {
  if (event.kind !== ROCKET_KIND || !HEX_64.test(event.pubkey)) return undefined;
  const identifier = tagValue(event, "d");
  if (!identifier) return undefined;
  const problem = problemReference(event);
  return {
    coordinate: `31108:${event.pubkey}:${identifier}`,
    identifier,
    author: event.pubkey,
    mission: tagValue(event, "mission"),
    ...(problem ? { problem } : {}),
    createdAt: event.created_at,
    eventId: event.id
  };
}

/**
 * Deduplicate kind 31108 events into the latest head per rocket coordinate.
 * The newest `created_at` wins; identical timestamps fall back to the
 * lexically larger event id, matching the replaceable-event rule.
 */
export function rocketsFromEvents(results: readonly RelayEventResult[]): Rocket[] {
  const latest = new Map<string, Rocket>();
  for (const { event } of results) {
    const rocket = parseRocket(event);
    if (!rocket) continue;
    const current = latest.get(rocket.coordinate);
    if (!current || rocket.createdAt > current.createdAt
      || (rocket.createdAt === current.createdAt && rocket.eventId > current.eventId)) {
      latest.set(rocket.coordinate, rocket);
    }
  }
  return [...latest.values()].sort((left, right) =>
    left.identifier.localeCompare(right.identifier) || left.coordinate.localeCompare(right.coordinate));
}

/**
 * Index rockets by the problem coordinate they reference, so a problem card can
 * ask "which rockets are solving me?". Rockets without a problem link are not
 * indexed anywhere.
 */
export function rocketsByProblem(rockets: readonly Rocket[]): Map<string, Rocket[]> {
  const index = new Map<string, Rocket[]>();
  for (const rocket of rockets) {
    if (!rocket.problem) continue;
    const list = index.get(rocket.problem.coordinate) ?? [];
    list.push(rocket);
    index.set(rocket.problem.coordinate, list);
  }
  for (const list of index.values()) {
    list.sort((left, right) => left.identifier.localeCompare(right.identifier) || left.coordinate.localeCompare(right.coordinate));
  }
  return index;
}

/** Display name for a rocket: its `d` identifier, trimmed to something readable. */
export function rocketLabel(rocket: Rocket): string {
  return rocket.identifier.trim() || "Rocket";
}
